import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import CandidatePresentationPanel from "./CandidatePresentationPanel";
import { portalRequest } from "../utils/clientPortal";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { IosShareOutlined as IosShareOutlinedIcon } from "./icons/FigmaControlIcons";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import {
  copyTextToClipboard,
  SHARE_LINK_COPIED_MESSAGE,
} from "../utils/publicShareLinks";

const RAW_API_BASE = (process.env.REACT_APP_API_BASE || "").trim();
const PURPLE = "#9C4FFF";
const BLUE = "#3451F4";
const BORDER = "#D0D5DD";
const MUTED = "#98A2B3";

function buildApiUrl(pathname) {
  const path = pathname?.startsWith("/") ? pathname : `/${pathname || ""}`;
  let base = RAW_API_BASE ? RAW_API_BASE.replace(/\/$/, "") : "/api";
  const hasApiSuffix = base === "/api" || base.endsWith("/api") || base.includes("/api/");
  if (!hasApiSuffix) base = `${base}/api`;
  return `${base}${path}`;
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    ""
  );
}

function safeTrim(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function joinUrl(origin, path) {
  const cleanOrigin = safeTrim(origin).replace(/\/$/, "");
  const cleanPath = safeTrim(path);
  if (!cleanPath) return cleanOrigin;
  if (/^https?:\/\//i.test(cleanPath)) return cleanPath;
  return `${cleanOrigin}${cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`}`;
}

export default function CandidateShareDialog({
  open,
  onClose,
  candidate,
}) {
  const candidateId = candidate?.candidate_id || candidate?.id || null;
  const candidateName = safeTrim(candidate?.full_name || candidate?.name || "Candidate");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [mode, setMode] = useState("client");
  const [clientJobs, setClientJobs] = useState(null);
  const [clientLookupId, setClientLookupId] = useState(null);
  const [clientError, setClientError] = useState("");
  useEffect(() => {
    if (!open || !candidateId) return;
    let active = true;
    setMode("client"); setClientJobs(null); setClientLookupId(null); setClientError("");
    portalRequest(`/candidates/${candidateId}/jobs`, { staff: true })
      .then((data) => { if (active) { setClientJobs(data.jobs); setClientLookupId(candidateId); if (!data.jobs.length) setMode("public"); } })
      .catch((e) => { if (active) { setClientError(e.message); setClientLookupId(candidateId); } });
    return () => { active = false; };
  }, [open, candidateId]);

  const summaryItems = useMemo(
    () =>
      [
        candidateName,
        candidate?.title || candidate?.candidate_title || "",
        candidate?.current_company || candidate?.currentCompany || candidate?.company || "",
        candidate?.location || "",
      ]
        .map((value) => safeTrim(value))
        .filter(Boolean)
        .filter((value, index, list) => list.indexOf(value) === index),
    [candidate, candidateName]
  );

  const shareUrl = useMemo(() => {
    const publicPath = safeTrim(shareData?.publicPath);
    if (!publicPath) return "";
    if (typeof window === "undefined") return publicPath;
    return joinUrl(window.location.origin, publicPath);
  }, [shareData?.publicPath]);

  useEffect(() => {
    if (!open || mode !== "public" || clientLookupId !== candidateId || !candidateId || shareData?.candidateId === candidateId) return;

    let active = true;
    const token = getToken();

    setLoading(true);
    setError("");
    setCopied(false);

    fetch(buildApiUrl(`/candidates/${encodeURIComponent(candidateId)}/share-profile`), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(token ? { "x-access-token": token } : {}),
      },
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || "Failed to create share link.");
        }
        return payload;
      })
      .then((payload) => {
        if (!active) return;
        setShareData({
          candidateId,
          headline: safeTrim(payload?.share?.headline),
          publicPath: safeTrim(payload?.share?.publicPath),
        });
      })
      .catch((fetchError) => {
        if (!active) return;
        console.error("Failed to create candidate share link:", fetchError);
        setError(fetchError?.message || "Failed to create share link.");
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [candidateId, open, mode, clientLookupId, shareData?.candidateId]);

  useEffect(() => {
    if (open) return;
    setMode("client");
    setClientLookupId(null);
    setCopied(false);
    setError("");
  }, [open]);

  const handleCopy = async () => {
    if (!shareUrl) return;

    try {
      await copyTextToClipboard(shareUrl);
      setCopied(true);
    } catch (copyError) {
      console.error("Failed to copy candidate share link:", copyError);
      setError("Could not copy the link automatically. You can still copy it from the field.");
    }
  };

  const handleOpenPublicProfile = () => {
    if (!shareUrl || typeof window === "undefined") return;
    window.open(shareUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          border: "1px solid rgba(156, 79, 255, 0.12)",
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.18)",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            px: { xs: 2.5, sm: 3.5 },
            py: { xs: 2.5, sm: 3 },
            background:
              "linear-gradient(135deg, rgba(52,81,244,0.08) 0%, rgba(156,79,255,0.12) 100%)",
            borderBottom: "1px solid rgba(208,213,221,0.7)",
          }}
        >
          <Box data-popup-header sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "12px",
                    backgroundColor: PURPLE,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                  }}
                >
                  <IosShareOutlinedIcon sx={{ fontSize: 18 }} />
                </Box>
                <Typography data-popup-title
                  sx={{
                    fontFamily: "Be Vietnam Pro",
                    fontSize: 18,
                    fontWeight: 800,
                    color: "#101828",
                  }}
                >
                  Share Candidate Profile
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontFamily: "Be Vietnam Pro",
                  fontSize: 13.5,
                  lineHeight: 1.6,
                  color: "#475467",
                  maxWidth: 560,
                }}
              >
                Present candidates to authenticated Clients in their portal, or create a limited public profile for prospect outreach.
              </Typography>
            </Box>

            <IconButton data-popup-close
              onClick={onClose}
              sx={{
                width: 34,
                height: 34,
                borderRadius: "999px",
                color: "#475467",
                backgroundColor: "#FFFFFF",
                border: "1px solid rgba(208,213,221,0.9)",
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <Tabs value={mode} onChange={(_, value) => setMode(value)} variant="fullWidth" aria-label="Candidate sharing destination">
          <Tab value="client" label="Client presentation" /><Tab value="public" label="Public share" />
        </Tabs>
        {mode === "client" && <Box sx={{ p: 3 }}>
          {clientError ? <Alert severity="error">{clientError}</Alert> : clientJobs === null ? <CircularProgress size={24} /> :
            <CandidatePresentationPanel key={candidateId} candidateId={candidateId} jobs={clientJobs} />}
        </Box>}
        <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3, display: mode === "public" ? "block" : "none" }}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2.5 }}>
            {summaryItems.map((item) => (
              <Chip
                key={item}
                label={item}
                sx={{
                  height: 30,
                  borderRadius: "999px",
                  backgroundColor: "#EEF4FF",
                  color: BLUE,
                  fontWeight: 600,
                  fontSize: 12.5,
                  fontFamily: "Be Vietnam Pro",
                }}
              />
            ))}
          </Stack>

          {error ? (
            <Alert severity="error" sx={{ mb: 2.5 }}>
              {error}
            </Alert>
          ) : null}

          {copied ? (
            <Alert severity="success" sx={{ mb: 2.5 }}>
              {SHARE_LINK_COPIED_MESSAGE}
            </Alert>
          ) : null}

          <Typography
            sx={{
              mb: 0.75,
              fontSize: 13,
              fontWeight: 700,
              color: "#344054",
              fontFamily: "Be Vietnam Pro",
            }}
          >
            Public profile link
          </Typography>

          <TextField
            fullWidth
            value={loading ? "Generating secure share link..." : shareUrl}
            placeholder="Generating secure share link..."
            InputProps={{
              readOnly: true,
            }}
            sx={{
              mb: 1.75,
              "& .MuiOutlinedInput-root": {
                minHeight: 52,
                borderRadius: "14px",
                backgroundColor: "#FFFFFF",
                boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
                "& fieldset": { borderColor: BORDER },
              },
              "& .MuiInputBase-input": {
                fontSize: 13,
                fontFamily: "Be Vietnam Pro",
                color: loading ? MUTED : "#475467",
              },
            }}
          />

          {shareData?.headline ? (
            <Box
              sx={{
                mb: 2.5,
                p: 1.75,
                borderRadius: "14px",
                border: "1px solid rgba(52, 81, 244, 0.12)",
                backgroundColor: "#F8FAFF",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Be Vietnam Pro",
                  fontWeight: 700,
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: BLUE,
                  mb: 0.5,
                }}
              >
                Public headline
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Be Vietnam Pro",
                  fontSize: 13.5,
                  lineHeight: 1.65,
                  color: "#344054",
                }}
              >
                {shareData.headline}
              </Typography>
            </Box>
          ) : null}

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            sx={{ alignItems: { xs: "stretch", sm: "center" } }}
          >
            <Button
              variant="contained"
              onClick={handleCopy}
              disabled={!shareUrl || loading}
              startIcon={
                loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <ContentCopyOutlinedIcon />
              }
              sx={{
                minHeight: 44,
                borderRadius: "999px",
                px: 2.5,
                backgroundColor: PURPLE,
                fontFamily: "Be Vietnam Pro",
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: PURPLE,
                  boxShadow: "none",
                },
              }}
            >
              {loading ? "Creating Link" : "Copy Link"}
            </Button>

            <Button
              variant="outlined"
              onClick={handleOpenPublicProfile}
              disabled={!shareUrl || loading}
              startIcon={<OpenInNewOutlinedIcon />}
              sx={{
                minHeight: 44,
                borderRadius: "999px",
                px: 2.5,
                borderColor: "rgba(52,81,244,0.22)",
                color: BLUE,
                fontFamily: "Be Vietnam Pro",
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Open Public Page
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
